import React, { useState, useEffect } from 'react';
import {
  get as getLabels,
  create as createLabel,
  formatLabelsTree,
} from '@api/v4/resources/labels';
import { move, destroy, update } from '@api/v4/labels';
import SortableTree from 'react-sortable-tree';
import AddLabelModal from './add_label_modal';
import ManageLabelModal from './manage_label_modal';
import 'react-sortable-tree/style.css';
import './styles/main';

function LabelTree() {
  const [labels, setLabels] = useState([]);
  const [updatedNodes, refreshTreeData] = useState([]);
  const [activeLabel, activateLabel] = useState({});
  const [showModal, toggleModal] = useState(false);
  const [expandedLabels, toggleExpanded] = useState([]);

  const formatTreeData = (labelData) => (
    Object.keys(labelData).map((labelKey) => {
      const label = labelData[labelKey];
      const sortableLabel = Object.assign(label.attributes,
        { title: label.attributes.name, key: labelKey });
      delete label.attributes;
      if (Object.keys(label).length) {
        sortableLabel.children = formatTreeData(label, true);
      }
      if (expandedLabels.includes(labelKey)) {
        sortableLabel.expanded = true;
      }
      return sortableLabel;
    })
  );

  useEffect(() => {
    getLabels().then(formatLabelsTree).then((labelsTree) => {
      const reactSortableTree = formatTreeData(labelsTree);
      setLabels(reactSortableTree);
    });
  }, [updatedNodes]);

  const moveNode = (treeData) => {
    const labelKeyToMove = treeData.node.key;
    const newParentKey = treeData.nextParentNode ? treeData.nextParentNode.key : null;
    const oldParentKey = treeData.node.path[treeData.node.path.length - 2] || null;
    const parentChanged = newParentKey !== oldParentKey;
    if (parentChanged) {
      move(labelKeyToMove, newParentKey).then(() => {
        setLabels(treeData.treeData);
      });
    }
  };

  const visibleToggle = (treeData) => {
    const labelKey = treeData.node.key;
    if (treeData.expanded) {
      toggleExpanded(expandedLabels.concat(labelKey));
    } else {
      toggleExpanded(expandedLabels.filter((key) => key !== labelKey));
    }
  };

  const openEditModal = (rowInfo) => {
    const { key, name } = rowInfo.node;
    activateLabel({ key, actionType: "Edit", name });
    toggleModal(true);
  };

  const editLabel = (key, updateLabelData) => {
    update(key, updateLabelData).then(() => {
      refreshTreeData(updatedNodes.concat(`edited ${key}`));
      activateLabel({});
      Notify.create({ type: "success", title: `Updated ${updateLabelData.name}` });
      toggleModal(false);
    });
  };

  const removeLabel = (key, labelName) => {
    destroy(key).then(() => {
      refreshTreeData(updatedNodes.concat(`removed ${key}`));
      activateLabel({});
      Notify.create({ type: "success", title: `Deleted ${labelName}` });
      toggleModal(false);
    });
  };

  const openAddModal = (rowInfo) => {
    const { key, name, path } = rowInfo.node;
    activateLabel({ actionType: "Add", parentKey: key, parentName: name, path });
    toggleModal(true);
  };

  const addLabel = (newLabelData) => {
    createLabel(newLabelData).then((data) => {
      const parentKey = newLabelData.parent_key;
      if (parentKey && !expandedLabels.includes(parentKey)) {
        toggleExpanded(expandedLabels.concat(parentKey));
      }
      refreshTreeData(updatedNodes.concat(`added ${data.id}`));
      toggleModal(false);
      activateLabel({});
      Notify.create({ type: "success", title: `Added ${newLabelData.name}` });
    });
  };

  const editButton = (rowInfo) => (
    <span
      className="bff-edit-asset"
      label="Edit"
      onClick={() => openEditModal(rowInfo)}
    />
  );

  const addButton = (rowInfo) => (
    <span
      label="Add"
      onClick={() => openAddModal(rowInfo)}
    >
      Add sub-label
    </span>
  );

  const renderModal = () => {
    const actionType = activeLabel.actionType;
    if (showModal && actionType && actionType.toLowerCase() === "add") {
      return (
        <AddLabelModal
          activeLabel={activeLabel}
          toggleModal={toggleModal}
          addLabel={addLabel}
        />
      );
    }
    if (showModal && actionType && actionType.toLowerCase() === "edit") {
      return (
        <ManageLabelModal
          activeLabel={activeLabel}
          toggleModal={toggleModal}
          removeLabel={removeLabel}
          editLabel={editLabel}
        />
      );
    }
    return null;
  };

  return (
    <div className="manage-labels">
      <h2>Manage Labels for {BF.fx.brandfolder().name}</h2>
      <hr />
      <div className="top-level-addition">
        <a onClick={() => openAddModal({ node: {} })}>
          <span className="bff-plus" />
          Add top level label
        </a>
      </div>
      <div style={{ height: '90vh' }}>
        <SortableTree
          treeData={labels}
          onChange={(treeData) => setLabels(treeData)}
          onMoveNode={moveNode}
          onVisibilityToggle={visibleToggle}
          generateNodeProps={(rowInfo) => ({
            buttons: [addButton(rowInfo), editButton(rowInfo)],
          })}
        />
        { renderModal() }
      </div>
    </div>
  );
}

export default LabelTree;
